// ============================================================================
// Adam Platform — Auth Route Guard
// ============================================================================
// Wraps protected routes; redirects to /login when no valid JWT is present.
// Also provides a reverse guard for the login page (redirect away if
// already authenticated).
// ============================================================================

import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

/**
 * Wrap around routes that require authentication.
 * Renders children when authenticated; redirects to /login otherwise.
 * Preserves the attempted URL so login can redirect back after success.
 */
export function RequireAuth({ children }: { children: React.ReactElement }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    // Save where the user was trying to go so we can redirect back after login
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return children;
}

/**
 * Wrap around the login route — if the user is already authenticated,
 * redirect them to the dashboard instead of showing the login form.
 */
export function RedirectIfAuth({ children }: { children: React.ReactElement }) {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to="/fleet" replace />;
  }

  return children;
}
